/**
 * Pseudo-Random Number Generator (PRNG)
 * Uses a Linear Congruential Generator (LCG) algorithm for deterministic randomness
 */
export class SeededRandom {
  private seed: number;

  constructor(seed: number) {
    this.seed = Math.abs(seed) % 2147483647; // Ensure positive 32-bit integer
    if (this.seed <= 0) {
      this.seed += 1;
    }
  }

  /**
   * Generate next pseudo-random number between 0 and 1
   */
  next(): number {
    this.seed = (this.seed * 16807) % 2147483647;
    return this.seed / 2147483647;
  }

  /**
   * Generate pseudo-random number in range [min, max)
   */
  range(min: number, max: number): number {
    return min + this.next() * (max - min);
  }

  /**
   * Generate pseudo-random integer in range [min, max]
   */
  intRange(min: number, max: number): number {
    return Math.floor(this.range(min, max + 1));
  }
}

/**
 * Create a hash from multiple values to use as a seed
 */
export function hashSeed(...values: (number | string)[]): number {
  let hash = 0;

  for (const value of values) {
    const str = String(value);
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i);
      hash = (hash << 5) - hash + char;
      hash = hash & hash; // Convert to 32-bit integer
    }
  }

  return Math.abs(hash);
}

/**
 * Get a deterministic random value between 0 and 1 for given inputs
 */
export function getSeededRandomValue(...values: (number | string)[]): number {
  const seed = hashSeed(...values);
  const rng = new SeededRandom(seed);
  return rng.next();
}
